// src/lib/arrears.ts — the arrears ladder: shapes and calls shared by the Arrears page, the
// tenancy page's arrears banner, Tenancies and the property page.
import { api } from './api';
import type { RentAccount } from './banking';

export type ActionKind = 'REMINDER' | 'PROMISE' | 'NOTE' | 'CALL' | 'NOTICE';
export type Channel = 'EMAIL' | 'DRAFT';

export interface LadderStep { step: 1 | 2 | 3 | 4; days: number; templateKey: string; label: string; subject: string }

export interface ArrearsRow {
  tenancyId: string; reference: string; payReference: string | null; tenants: string | null;
  unitId: string; unitReference: string | null; propertyId: string; propertyName: string; currency: string;
  oldestDueDate: string; daysOverdue: number; owedMinor: number; instalments: number;
  reachedStep: number; lastStepSent: number; nextStepDue: number | null; paused: boolean; pausedUntil: string | null; promiseBroken: boolean; dueNow: boolean;
  upcoming: { step: number; on: string } | null;
  nextStepLabel: string | null; lastStepLabel: string | null; lastReminderAt: string | null;
  promise: { promisedOn: string; amountMinor: number | null; broken: boolean; note: string | null } | null;
  lastAction: { kind: ActionKind; step: number | null; summary: string; at: string; byName: string | null } | null;
  replied: boolean; conversationId: string | null;
}

export interface ArrearsSummary {
  count: number; owedMinor: number; currency: string; dueNow: number; promisesOutstanding: number; promisesBroken: number; overMonth: number; replied: number;
  byStep: Array<{ step: number; label: string; count: number; owedMinor: number }>;
  byProperty: Array<{ propertyId: string; propertyName: string; count: number; owedMinor: number; dueNow: number }>;
}

export interface ArrearsAction {
  id: string; kind: ActionKind; step: number | null; stepLabel: string | null; channel: Channel | null; note: string | null;
  promisedOn: string | null; promisedAmountMinor: number | null; currency: string; owedMinor: number | null; daysOverdue: number | null;
  clears: boolean; outboxId: string | null; outboxStatus: string | null; conversationId: string | null; meta: Record<string, unknown>;
  createdAt: string; createdByName: string | null;
}

export interface ArrearsDetail {
  tenancyId: string; reference: string; inArrears: boolean; row: ArrearsRow | null;
  ladder: Array<LadderStep & { reached: boolean; due: boolean; sent: { at: string; byName: string | null; channel: string | null; outboxStatus: string | null } | null }>;
  instalments: Array<{ id: string; dueDate: string; amountMinor: number; paidMinor: number; owingMinor: number; status: string; daysOverdue: number }>;
  account: RentAccount;
  actions: ArrearsAction[];
  conversation: { id: string; subject: string; team: string; status: string; lastMessageAt: string; awaiting: boolean; lastResident: { body: string; at: string } | null } | null;
  recipients: Array<{ name: string | null; email: string | null }>;
}

export interface ReminderPreview {
  key: string | null; subject: string; body: string; missing: string[]; step: LadderStep;
  recipients: Array<{ name: string | null; email: string | null }>; alreadySent: boolean; paused: boolean;
}

export interface LadderRunItem { tenancyId: string; reference: string; unitReference: string | null; propertyName: string; tenants: string | null; owedMinor: number; daysOverdue: number; step: number | null; label: string | null }
export interface LadderRun {
  dryRun: boolean;
  sent: Array<LadderRunItem & { letters: Array<{ id: string; to: string | null; status: string }> }>;
  skipped: Array<LadderRunItem & { reason: string; detail: string }>;
  failed: Array<LadderRunItem & { error: string }>;
  counts: { due: number; sent: number; skipped: number; failed: number; letters: number };
}

export interface ArrearsFilters { propertyId?: string; minDays?: number; step?: number; due?: boolean; q?: string }

export const arrearsApi = {
  list: async (f: ArrearsFilters = {}) => (await api.get<ArrearsRow[]>('/arrears', { ...f, due: f.due ? 'true' : undefined })).data,
  summary: async (f: { propertyId?: string } = {}) => (await api.get<ArrearsSummary>('/arrears/summary', f)).data,
  get: async (tenancyId: string) => (await api.get<ArrearsDetail>(`/arrears/${tenancyId}`)).data,
  preview: async (tenancyId: string, step?: number) => (await api.get<ReminderPreview>(`/arrears/${tenancyId}/preview`, { step })).data,
  remind: async (tenancyId: string, body: { step?: number; channel?: Channel; note?: string | null }) =>
    (await api.post<{ step: LadderStep; sent: number; letters: Array<{ id: string; toEmail: string | null; status: string }>; conversationId: string | null }>(`/arrears/${tenancyId}/remind`, body)).data,
  promise: async (tenancyId: string, body: { promisedOn: string; amountMinor?: number | null; note?: string | null }) => (await api.post<ArrearsAction>(`/arrears/${tenancyId}/promise`, body)).data,
  note: async (tenancyId: string, body: { kind?: 'NOTE' | 'CALL' | 'NOTICE'; note: string }) => (await api.post<ArrearsAction>(`/arrears/${tenancyId}/note`, body)).data,
  run: async (body: { dryRun?: boolean; propertyId?: string }) => (await api.post<LadderRun>('/arrears/run', body)).data,
};

/** Ladder colours, as the prototype: grey under 7 days, blue 7+, amber 14+, red 21+, deep red 31+. */
/** Theme tokens (CSS variables), so callers tint with color-mix rather than hex alpha. */
export const daysColour = (days: number) => (days >= 31 ? 'var(--status-critical)' : days >= 21 ? 'var(--status-serious)' : days >= 14 ? 'var(--status-warning)' : days >= 7 ? 'var(--color-bronze-500)' : 'var(--status-neutral)');
export const STEP_SHORT: Record<number, string> = { 0: 'Under 7 days', 1: '1st reminder', 2: '2nd reminder', 3: '3rd reminder', 4: 'Final notice' };
